/**
 * @file index.tsx
 *
 * @fileoverview Renders a textarea using the styles provided in the theme.
 */

import { FormItem } from "./../../01-forms/11-form-item";
import { FormLabel } from "./../../01-forms/11-form-item/01-form-label";

import { FormDescription } from "./../../01-forms/11-form-item/02-form-description";
import React from "react";
import styles from "./textarea.module.scss";

export interface TextareaProps
  extends React.InputHTMLAttributes<HTMLTextAreaElement> {
  /**
   * The label for the textarea.
   */
  label?: string;
  /**
   * Marks if a field is in an error state.
   *
   * @default false
   */
  error?: boolean;
  /**
   * The number of rows for the textarea. Will determine the size for the textarea.
   *
   * @default 10
   */
  rows?: number;
  /**
   * The columns for the textarea.
   */
  columns?: number;
  /**
   * Description for this field.
   */
  description?: string;
}

/**
 * A textarea form element. Should be used for long text form elements. For larger or smaller
 * textareas make sure to manipulate the rows prop property.
 */
export const Textarea = ({
  label,
  id,
  description,
  rows = 10,
  columns,
  disabled = false,
  required = false,
  error = false,
  ...props
}: TextareaProps) => {
  if (!label) {
    return (
      <textarea
        className={styles.textarea}
        id={id}
        rows={rows}
        style={{
          borderColor: error
            ? `var(--color-danger)`
            : `var(--input-border-color)`,
          opacity: disabled ? 0.5 : 1,
        }}
        required={required}
        disabled={disabled}
        {...props}
      />
    );
  }
  return (
    <FormItem>
      <FormLabel htmlFor={id} error={error} required={required}>
        {label}
      </FormLabel>
      <textarea
        className={styles.textarea}
        id={id}
        rows={rows}
        style={{
          borderColor: error
            ? `var(--color-danger)`
            : `var(--input-border-color)`,
          opacity: disabled ? 0.5 : 1,
        }}
        required={required}
        disabled={disabled}
        {...props}
      />
      {description && <FormDescription>{description}</FormDescription>}
    </FormItem>
  );
};

export default Textarea;
